import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import PostCard from '@/components/CommunityPostCard';
import TickerSearchInput from '@/components/TickerSearchInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TUTORIAL_PREFILL = `# 📈 [TICKER]: Dividend Analysis & Investment Thesis

**Company Name:** 
**Current Stock Price:** $
**Current Dividend Yield:** %

---

### 1. The Dividend Core Metrics
- **Payout Ratio (Earnings):** ____% *(Is it under 70%?)*
- **Free Cash Flow (FCF) Payout Ratio:** ____% *(Is cash covering the dividend?)*
- **Dividend Growth Streak:** ____ Years
- **5-Year Dividend CAGR:** ____%

### 2. The Moat & Business Thesis
*Why will this company continue to make money and pay dividends for the next 10 years?*

`;

const TUTORIAL_HINT = `Below the fill-in section, expand with your own analysis. Suggested sections:
- 3. Risk Factors (why the dividend could be cut)
- 4. Valuation Check (current P/E vs 5-yr average, your verdict)
- 5. Bottom Line — Buy / Hold / Avoid and why

⚠️ Tutorials require ≥ 200 words and earn +100 community points on publish (capped at 2 per week).`;

function PostComposer({ onPosted, isMember }) {
  const [kind, setKind] = useState('stock');
  const [ticker, setTicker] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  if (!isMember) {
    return (
      <div className="rounded-xl border-2 border-dashed border-violet-300 dark:border-violet-700 bg-violet-50/30 dark:bg-violet-950/20 p-4 text-center text-sm text-muted-foreground">
        Join the community to post.
      </div>
    );
  }

  const handleKindChange = (newKind) => {
    setKind(newKind);
    // Pre-fill the tutorial template when user first switches to tutorial mode
    if (newKind === 'tutorial' && !body.trim()) {
      setBody(TUTORIAL_PREFILL);
    }
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Title is required.');
      return;
    }
    if (kind === 'stock' && !ticker.trim()) {
      toast.error('Ticker is required for stock discussions.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/community/posts`,
        {
          kind,
          title: title.trim(),
          body: body.trim(),
          ticker: kind === 'stock' ? ticker.trim().toUpperCase() : null,
        },
        { withCredentials: true },
      );
      setTitle('');
      setBody('');
      setTicker('');
      toast.success('Posted!');
      onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Post failed.');
    } finally {
      setBusy(false);
    }
  };

  const stockCls = kind === 'stock'
    ? 'px-3 py-1 rounded-full text-xs font-semibold transition bg-violet-600 text-white'
    : 'px-3 py-1 rounded-full text-xs font-semibold transition bg-muted text-muted-foreground hover:bg-muted/70';
  const generalCls = kind === 'general'
    ? 'px-3 py-1 rounded-full text-xs font-semibold transition bg-violet-600 text-white'
    : 'px-3 py-1 rounded-full text-xs font-semibold transition bg-muted text-muted-foreground hover:bg-muted/70';
  const tutorialCls = kind === 'tutorial'
    ? 'px-3 py-1 rounded-full text-xs font-semibold transition bg-violet-600 text-white'
    : 'px-3 py-1 rounded-full text-xs font-semibold transition bg-muted text-muted-foreground hover:bg-muted/70';

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="post-composer">
      <div className="flex gap-2 mb-3 flex-wrap">
        <button type="button" onClick={() => handleKindChange('stock')} className={stockCls} data-testid="composer-kind-stock">Stock Discussion</button>
        <button type="button" onClick={() => handleKindChange('general')} className={generalCls} data-testid="composer-kind-general">General</button>
        <button type="button" onClick={() => handleKindChange('tutorial')} className={tutorialCls} data-testid="composer-kind-tutorial">📑 Tutorial (+100 pts)</button>
      </div>
      {kind === 'stock' ? (
        <div className="mb-2 space-y-1">
          <TickerSearchInput
            value={ticker}
            onChange={setTicker}
            onSelect={(t) => setTicker(t)}
            placeholder="Search ticker (e.g. KO)"
            className="w-full sm:w-72"
            testid="composer-ticker"
          />
          {ticker ? (
            <p className="text-[11px] text-muted-foreground">
              Discussion ticker: <span className="font-mono font-bold text-foreground">{ticker}</span>
              {' '}
              <button
                type="button"
                onClick={() => setTicker('')}
                className="text-violet-600 dark:text-violet-400 hover:underline"
                data-testid="composer-ticker-clear"
              >
                change
              </button>
            </p>
          ) : null}
        </div>
      ) : null}
      <input
        type="text"
        placeholder={kind === 'tutorial' ? 'Tutorial title (e.g. "How to read a Payout Ratio")' : 'Title'}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full text-sm border border-border rounded p-2 bg-background font-semibold mb-2"
        maxLength={200}
        data-testid="composer-title-input"
      />
      <textarea
        placeholder={kind === 'tutorial' ? 'Fill in the template above…' : 'Share your take…'}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className={kind === 'tutorial' ? 'w-full text-sm border border-border rounded p-2 bg-background font-mono' : 'w-full text-sm border border-border rounded p-2 bg-background'}
        rows={kind === 'tutorial' ? 10 : 3}
        maxLength={8000}
        data-testid="composer-body-input"
      />
      {kind === 'tutorial' ? (
        <div className="mt-2 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-950/20 p-3" data-testid="composer-tutorial-hint">
          <p className="text-[11px] text-violet-700 dark:text-violet-300 whitespace-pre-line leading-relaxed">{TUTORIAL_HINT}</p>
        </div>
      ) : null}
      <div className="flex items-center justify-between mt-2 gap-2">
        <p className="text-[10px] text-muted-foreground flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Language filter is on. Please be kind.
        </p>
        <Button size="sm" onClick={submit} disabled={busy} data-testid="composer-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post'}
        </Button>
      </div>
    </div>
  );
}

const SORT_TABS = [
  { key: 'hot', label: 'Hot' },
  { key: 'new', label: 'New' },
  { key: 'top', label: 'Top' },
];
const KIND_TABS = [
  { key: 'all', label: 'All' },
  { key: 'stock', label: 'Stock' },
  { key: 'tutorial', label: 'Tutorials' },
  { key: 'general', label: 'General' },
];

function FeedToolbar({ sort, setSort, kindFilter, setKindFilter }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex gap-1 bg-muted rounded-lg p-1">
        {SORT_TABS.map((s) => {
          const cls = sort === s.key
            ? 'px-3 py-1 text-xs font-semibold rounded-md transition bg-background shadow-sm text-foreground'
            : 'px-3 py-1 text-xs font-semibold rounded-md transition text-muted-foreground hover:text-foreground';
          return (
            <button key={s.key} type="button" onClick={() => setSort(s.key)} className={cls} data-testid={`feed-sort-${s.key}`}>
              {s.label}
            </button>
          );
        })}
      </div>
      <div className="flex gap-1">
        {KIND_TABS.map((k) => {
          const cls = kindFilter === k.key
            ? 'px-2.5 py-1 text-xs font-semibold rounded-full transition border bg-violet-600 text-white border-violet-600'
            : 'px-2.5 py-1 text-xs font-semibold rounded-full transition border bg-background text-muted-foreground hover:text-foreground border-border';
          return (
            <button key={k.key} type="button" onClick={() => setKindFilter(k.key)} className={cls} data-testid={`feed-kind-${k.key}`}>
              {k.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CommunityFeedTab({ me }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tickerParam = (searchParams.get('ticker') || '').trim().toUpperCase();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('hot');
  const [kindFilter, setKindFilter] = useState('all');
  const isMember = !!me?.is_member;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, limit: 50 };
      if (kindFilter !== 'all') params.kind = kindFilter;
      if (tickerParam) {
        params.ticker = tickerParam;
        // Force kind=stock when filtering by ticker so general posts don't slip in
        params.kind = 'stock';
      }
      const res = await axios.get(`${API}/community/posts`, { params, withCredentials: true });
      setPosts(res.data.posts || []);
    } catch (e) {
      toast.error('Failed to load posts.');
    } finally {
      setLoading(false);
    }
  }, [sort, kindFilter, tickerParam]);

  useEffect(() => { load(); }, [load]);

  const clearTickerFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('ticker');
    setSearchParams(next);
  };

  return (
    <div className="space-y-4" data-testid="community-feed-tab">
      <PostComposer onPosted={load} isMember={isMember} />
      {tickerParam ? (
        <div className="rounded-lg bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 px-3 py-2 text-xs font-semibold flex items-center justify-between gap-2" data-testid="feed-ticker-filter-pill">
          <span>Filtering by ${tickerParam}</span>
          <button
            type="button"
            onClick={clearTickerFilter}
            className="p-0.5 rounded hover:bg-violet-200 dark:hover:bg-violet-900/40"
            aria-label="Clear ticker filter"
            data-testid="feed-clear-ticker-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : null}
      <FeedToolbar sort={sort} setSort={setSort} kindFilter={kindFilter} setKindFilter={setKindFilter} />
      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading feed…
        </div>
      ) : null}
      {!loading && posts.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
          {tickerParam ? `No community posts about $${tickerParam} yet — be the first to start the conversation!` : 'No posts yet. Start the conversation!'}
        </div>
      ) : null}
      {!loading && posts.map((p) => (
        <PostCard key={p.post_id} post={p} isMember={isMember} onMutate={load} />
      ))}
    </div>
  );
}
